import axios from "axios";

const apiUrl = `${process.env.REACT_APP_API_URL}/api/group`;

function getToken() {
  return localStorage.getItem("token");
}

function getAuthHeaders({ required = false } = {}) {
  const token = getToken();

  if (!token) {
    if (required) {
      throw new Error("Utilisateur non authentifié. Token manquant.");
    }

    return {};
  }

  return {
    Authorization: `Bearer ${token}`,
  };
}

function getAxiosConfig({ authRequired = false } = {}) {
  const headers = getAuthHeaders({ required: authRequired });

  return Object.keys(headers).length > 0 ? { headers } : {};
}

function handleAxiosError(
  error,
  fallbackMessage = "Erreur interne du serveur.",
) {
  if (error?.response?.data) {
    throw error.response.data;
  }

  throw {
    error: error?.message || fallbackMessage,
    message: error?.message || fallbackMessage,
  };
}

export function extractGroupIdFromInviteLink(value) {
  const rawValue = String(value || "").trim();

  if (!rawValue) return null;

  if (/^\d+$/.test(rawValue)) {
    return rawValue;
  }

  try {
    const url = new URL(rawValue);
    const match = url.pathname.match(/\/join-group\/(\d+)/);

    if (match?.[1]) return match[1];
  } catch (error) {
    const match = rawValue.match(/join-group\/(\d+)/);

    if (match?.[1]) return match[1];
  }

  return null;
}

/// GET
// Fonction pour récupérer les groupes d'un utilisateur avec son userId
export const getGroupsByUser = async (userId) => {
  try {
    const response = await axios.get(
      `${apiUrl}/getGroupsByUser/${userId}`,
      getAxiosConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement des groupes:", error);
    handleAxiosError(error, "Erreur lors du chargement des groupes.");
  }
};

// Fonction pour récupérer le nombre d'adhérents par groupe et le détail des adhérents
export const getGroupAdherents = async (groupId) => {
  try {
    const response = await axios.get(
      `${apiUrl}/getGroupAdherents/${groupId}`,
      getAxiosConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement des adhérents du groupe:", error);
    handleAxiosError(
      error,
      "Erreur lors du chargement des adhérents du groupe.",
    );
  }
};

// Fonction pour récupérer les groupes pour GroupeTable
export const getGroupsHome = async () => {
  try {
    const response = await axios.get(
      `${apiUrl}/allgroupsHome`,
      getAxiosConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement des groupes:", error);
    handleAxiosError(error, "Erreur lors du chargement des groupes.");
  }
};

// Fonction pour récupérer le nombre de groupes pour HeaderCount
export const getGroupsCount = async () => {
  try {
    const response = await axios.get(`${apiUrl}/groupCount`, getAxiosConfig());

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement du nombre de groupes:", error);
    handleAxiosError(error, "Erreur lors du chargement du nombre de groupes.");
  }
};

// Fonction pour récupérer tous les groupes et utilisateurs créateurs
export const getAllGroups = async () => {
  try {
    const response = await axios.get(`${apiUrl}/getGroups`, getAxiosConfig());

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement des groupes:", error);
    handleAxiosError(error, "Erreur lors du chargement des groupes.");
  }
};

// Fonction pour récupérer tous les groupes nom + id pour l'envoi de messages groupés
export const getAllGroupsMessage = async () => {
  try {
    const response = await axios.get(
      `${apiUrl}/allgroupsMessage`,
      getAxiosConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement des groupes message:", error);
    handleAxiosError(error, "Erreur lors du chargement des groupes.");
  }
};

// Fonction pour récupérer le nom du groupe d'un adhérent
export const getGroupName = async (adherentId) => {
  try {
    const response = await axios.get(
      `${apiUrl}/findGroup/${adherentId}`,
      getAxiosConfig(),
    );

    return response.data?.groupNames ? response.data.groupNames : [];
  } catch (error) {
    console.error("Erreur lors du chargement du nom du groupe:", error);
    return [];
  }
};

// Fonction pour récupérer la liste des groupes et le nombre d'adhérents par groupe
export const getGroupsList = async () => {
  try {
    const response = await axios.get(`${apiUrl}/listGroup`, getAxiosConfig());

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement de la liste des groupes:", error);
    handleAxiosError(error, "Erreur lors du chargement des groupes.");
  }
};

// Fonction pour récupérer la liste des groupes actifs et le nombre d'adhérents par groupe
export const getActiveGroupsList = async () => {
  try {
    const response = await axios.get(
      `${apiUrl}/listGroupActive`,
      getAxiosConfig(),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement des groupes actifs:", error);
    handleAxiosError(error, "Erreur lors du chargement des groupes actifs.");
  }
};

// Fonction pour compter le nombre de groupes
export const countGroups = async () => {
  try {
    const response = await axios.get(`${apiUrl}/countGroups`, getAxiosConfig());

    return response.data;
  } catch (error) {
    console.error("Erreur lors du comptage des groupes:", error);
    handleAxiosError(error, "Erreur lors du comptage des groupes.");
  }
};

/// POST
// Fonction pour créer un groupe
export const createGroup = async (group) => {
  try {
    const response = await axios.post(
      `${apiUrl}/addGroup`,
      group,
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la création du groupe:", error);
    handleAxiosError(error, "Erreur lors de la création du groupe.");
  }
};

// Fonction pour ajouter un adhérent à un groupe manuellement
export const addAdherentToGroupManually = async (
  groupId,
  adherentId,
  status = "approved",
) => {
  try {
    const response = await axios.post(
      `${apiUrl}/${groupId}/adherents/manual`,
      {
        adherent_id: adherentId,
        status,
      },
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors du rattachement manuel au groupe :", {
      status: error?.response?.status,
      data: error?.response?.data,
      url: error?.config?.url,
      method: error?.config?.method,
      requestData: error?.config?.data,
    });

    handleAxiosError(error, "Erreur lors du rattachement manuel au groupe.");
  }
};

/// PUT
// Fonction pour modifier les informations d'un adhérent depuis un groupe coach
export const updateGroupAdherent = async (groupId, adherentId, payload) => {
  try {
    const response = await axios.put(
      `${apiUrl}/${groupId}/adherent/${adherentId}`,
      payload,
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la modification de l'adhérent :", {
      status: error?.response?.status,
      data: error?.response?.data,
      url: error?.config?.url,
      method: error?.config?.method,
      requestData: error?.config?.data,
    });

    handleAxiosError(error, "Erreur lors de la modification de l'adhérent.");
  }
};

// Fonction pour déplacer un adhérent d'un groupe vers un autre groupe du coach
export const moveAdherentToAnotherGroup = async (
  currentGroupId,
  targetGroupId,
  adherentId,
  status = "approved",
) => {
  try {
    const response = await axios.put(
      `${apiUrl}/${currentGroupId}/adherent/${adherentId}/move`,
      {
        target_group_id: targetGroupId,
        status,
      },
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors du déplacement de l'adhérent :", {
      status: error?.response?.status,
      data: error?.response?.data,
      url: error?.config?.url,
      method: error?.config?.method,
      requestData: error?.config?.data,
    });

    handleAxiosError(error, "Erreur lors du déplacement de l'adhérent.");
  }
};

// Fonction pour modifier un groupe
export const updateGroup = async (group) => {
  try {
    const response = await axios.put(
      `${apiUrl}/updateGroup/${group.id}`,
      group,
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour du groupe:", error);
    handleAxiosError(error, "Erreur lors de la mise à jour du groupe.");
  }
};

// Fonction pour modifier l'ordre des groupes
export const updateOrderGroup = async (groups) => {
  try {
    const response = await axios.put(
      `${apiUrl}/updateOrderGroup`,
      { groups },
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la mise à jour de l'ordre des groupes:",
      error,
    );

    handleAxiosError(
      error,
      "Erreur lors de la mise à jour de l'ordre des groupes.",
    );
  }
};

// Fonction pour activer ou désactiver un groupe
export const activateGroup = async (groupId) => {
  try {
    const response = await axios.put(
      `${apiUrl}/activateGroup/${groupId}`,
      {},
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de l'activation / désactivation du groupe:",
      error,
    );
    handleAxiosError(error, "Erreur lors de la mise à jour du groupe.");
  }
};

// Fonction pour modifier le statut d'un adhérent dans un groupe
export const updateAdherentStatus = async (adherentId, groupId, status) => {
  try {
    const response = await axios.put(
      `${apiUrl}/${groupId}/adherent/${adherentId}/status`,
      { status },
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour du statut :", error);
    handleAxiosError(error, "Erreur lors de la mise à jour du statut.");
  }
};

/// DELETE
// Fonction pour supprimer un groupe
export const deleteGroup = async (groupId) => {
  try {
    const response = await axios.delete(
      `${apiUrl}/removeGroup/${groupId}`,
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la suppression du groupe:", error);
    handleAxiosError(error, "Erreur lors de la suppression du groupe.");
  }
};

// Fonction pour supprimer un adhérent d'un groupe
export const deleteAdherent = async (adherentId, groupId) => {
  try {
    const response = await axios.delete(
      `${apiUrl}/${groupId}/adherent/${adherentId}`,
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la suppression de l'adhérent :", error);
    handleAxiosError(error, "Erreur lors de la suppression de l'adhérent.");
  }
};

/// INVITATIONS JUDOKAS SUR GROUPES
// Fonction pour générer une invitation pour un groupe
export const generateInvite = async (groupId) => {
  try {
    const response = await axios.post(
      `${apiUrl}/${groupId}/invite`,
      {},
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la génération de l'invitation :", error);
    handleAxiosError(error, "Erreur lors de la génération de l'invitation.");
  }
};

// Fonction pour rejoindre un groupe via son groupId
export const joinGroup = async (groupId) => {
  try {
    const finalGroupId = Number(groupId);

    if (!finalGroupId) {
      throw new Error("Identifiant de groupe invalide.");
    }

    const response = await axios.post(
      `${apiUrl}/join/${finalGroupId}`,
      {},
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la demande de rattachement au groupe:",
      error,
    );
    handleAxiosError(
      error,
      "Erreur lors de la demande de rattachement au groupe.",
    );
  }
};

// Fonction pour rejoindre un groupe via un lien d'invitation
export const joinGroupByLink = async (inviteLink, adherentId) => {
  try {
    const groupId = extractGroupIdFromInviteLink(inviteLink);

    if (!groupId) {
      throw new Error("Lien d'invitation invalide.");
    }

    if (!adherentId) {
      throw new Error("Veuillez sélectionner le judoka concerné.");
    }

    const response = await axios.post(
      `${apiUrl}/join/${groupId}`,
      {
        adherent_id: adherentId,
      },
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de l'ajout au groupe via le lien :", error);
    handleAxiosError(error, "Erreur lors de l'ajout au groupe via le lien.");
  }
};

// Fonction pour récupérer les demandes du judoka connecté
export const getUserRequests = async () => {
  try {
    const response = await axios.get(
      `${apiUrl}/user/requests`,
      getAxiosConfig({ authRequired: true }),
    );

    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error("Erreur lors de la récupération des demandes :", error);
    handleAxiosError(error, "Erreur lors de la récupération des demandes.");
  }
};

// Fonction pour quitter un groupe avec le compte connecté
export const leaveCurrentUserGroup = async (groupId) => {
  try {
    const finalGroupId = Number(groupId);

    if (!finalGroupId) {
      throw new Error("Identifiant de groupe invalide.");
    }

    const response = await axios.delete(
      `${apiUrl}/${finalGroupId}/me`,
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la sortie du groupe :", error);
    handleAxiosError(error, "Erreur lors de la sortie du groupe.");
  }
};

// Fonction pour récupérer les demandes d'un groupe
export const getGroupRequests = async (groupId) => {
  try {
    const response = await axios.get(
      `${apiUrl}/requests/${groupId}`,
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la récupération des demandes du groupe :",
      error,
    );
    handleAxiosError(error, "Erreur lors de la récupération des demandes.");
  }
};

// Fonction pour valider une demande
export const validateRequest = async (requestId, status) => {
  try {
    const response = await axios.patch(
      `${apiUrl}/requests/${requestId}`,
      { status },
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la validation de la demande :", error);
    handleAxiosError(error, "Erreur lors de la validation de la demande.");
  }
};

// Fonction pour récupérer les invitations / demandes d'un groupe
export const getInvites = async (groupId) => {
  try {
    const response = await axios.get(
      `${apiUrl}/requests/${groupId}`,
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la récupération des invitations :", error);
    handleAxiosError(error, "Erreur lors de la récupération des invitations.");
  }
};

// Fonction pour révoquer une invitation
// Attention : il faut que la route backend existe : DELETE /api/group/revokeInvite/:inviteCode
export const revokeInvite = async (inviteCode) => {
  try {
    const response = await axios.delete(
      `${apiUrl}/revokeInvite/${inviteCode}`,
      getAxiosConfig({ authRequired: true }),
    );

    return response.data;
  } catch (error) {
    console.error("Erreur lors de la révocation de l'invitation:", error);
    handleAxiosError(error, "Erreur lors de la révocation de l'invitation.");
  }
};
